import React, { useState, useEffect } from 'react';
import AIContentGenerator from './AIContentGenerator';
import ContentApprovalQueue from './ContentApprovalQueue';
import AIServiceStatus from './AIServiceStatus';
import MarketingDashboard from './MarketingDashboard';

const CEODashboard = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [showCEOPassword, setShowCEOPassword] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [loginError, setLoginError] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    
    const [metrics, setMetrics] = useState(null);
    const [aiDecisions, setAiDecisions] = useState([]);
    const [systemStatus, setSystemStatus] = useState(null);
    const [emergencyStop, setEmergencyStop] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [draftContent, setDraftContent] = useState([]);

    // Handler for when new content is generated
    const handleContentGenerated = (content) => {
        const updatedContent = [...draftContent, content];
        setDraftContent(updatedContent);
        localStorage.setItem('ai_draft_content', JSON.stringify(updatedContent));
    };

    useEffect(() => {
        // Check for "Remember Me" session
        const crmRememberMe = localStorage.getItem('pricepalcompare_crm_remember_me') === 'true';
        const lastActivity = localStorage.getItem('pricepalcompare_crm_last_activity');
        
        if (crmRememberMe && lastActivity) {
            const timeSinceActivity = Date.now() - parseInt(lastActivity);
            const SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours
            
            // If session is valid, try to fetch data
            if (timeSinceActivity < SESSION_TIMEOUT) {
                fetchDashboardData().then(() => {
                    setIsAuthenticated(true);
                    localStorage.setItem('pricepalcompare_crm_last_activity', Date.now().toString());
                }).catch(() => {
                    // If fetch fails, clear session and require login
                    localStorage.removeItem('pricepalcompare_crm_remember_me');
                    localStorage.removeItem('pricepalcompare_crm_last_activity');
                    setIsAuthenticated(false);
                    setLoading(false);
                });
            } else {
                // Session expired
                localStorage.removeItem('pricepalcompare_crm_remember_me');
                localStorage.removeItem('pricepalcompare_crm_last_activity');
                setIsAuthenticated(false);
                setLoading(false);
            }
        } else {
            // No remember me or expired - require login
            setIsAuthenticated(false);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            const interval = setInterval(fetchDashboardData, 30000); // Refresh every 30s
            return () => clearInterval(interval);
        }
    }, [isAuthenticated]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        setLoginLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    username,
                    password
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Login failed');
            }

            setIsAuthenticated(true);
            
            // Store Remember Me preference
            if (rememberMe) {
                localStorage.setItem('pricepalcompare_crm_remember_me', 'true');
                localStorage.setItem('pricepalcompare_crm_last_activity', Date.now().toString());
            } else {
                localStorage.removeItem('pricepalcompare_crm_remember_me');
                localStorage.removeItem('pricepalcompare_crm_last_activity');
            }
            
            await fetchDashboardData();
        } catch (error) {
            setLoginError(error.message || 'Login failed');
        } finally {
            setLoginLoading(false);
        }
    };

    const handleLogout = () => {
        setIsAuthenticated(false);
        setUsername('');
        setPassword('');
        
        // Clear Remember Me data
        localStorage.removeItem('pricepalcompare_crm_remember_me');
        localStorage.removeItem('pricepalcompare_crm_last_activity');
        
    };

    const fetchDashboardData = async () => {
        try {
            const headers = {
                'Content-Type': 'application/json'
            };

            const [metricsRes, decisionsRes, statusRes] = await Promise.all([
                fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/dashboard/metrics`, { 
                    headers,
                    credentials: 'include'  // Include cookies for authentication
                }),
                fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/dashboard/ai-decisions`, { 
                    headers,
                    credentials: 'include'
                }),
                fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/dashboard/system-status`, { 
                    headers,
                    credentials: 'include'
                })
            ]);

            if (metricsRes.ok && decisionsRes.ok && statusRes.ok) {
                const metricsData = await metricsRes.json();
                const decisionsData = await decisionsRes.json();
                const statusData = await statusRes.json();

                setMetrics(metricsData.metrics);
                setAiDecisions(decisionsData.decisions);
                setSystemStatus(statusData.system_status);
                setEmergencyStop(statusData.system_status?.automation?.is_stopped || false);
            }
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
            setLoading(false);
            throw error; // Re-throw to handle in auth check
        }
    };

    const handleEmergencyStop = async () => {
        if (!window.confirm('⚠️ Stop all automated CRM activities immediately?')) return;

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/dashboard/control/emergency-stop`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',  // Include cookies for authentication
                body: JSON.stringify({ reason: 'Manual emergency stop from CEO Dashboard' })
            });

            if (response.ok) {
                setEmergencyStop(true);
                alert('✅ All automated CRM activities stopped');
                fetchDashboardData();
            }
        } catch (error) {
            alert('❌ Failed to stop CRM activities');
        }
    };

    const handleResume = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/dashboard/control/resume`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'  // Include cookies for authentication
            });

            if (response.ok) {
                setEmergencyStop(false);
                alert('✅ CRM activities resumed');
                fetchDashboardData();
            }
        } catch (error) {
            alert('❌ Failed to resume CRM activities');
        }
    };

    // Login Screen
    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-purple-600 mb-2">PricePAL Compare CRM</h1>
                        <p className="text-gray-600">Customer Relationship Management</p>
                    </div>

                    <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="admin"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                            <div className="relative">
                                <input
                                    type={showCEOPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder="••••••••"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowCEOPassword(v => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    tabIndex={-1}
                                >
                                    {showCEOPassword ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                            <label className="flex items-center space-x-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className="w-4 h-4 text-purple-600 focus:ring-purple-500 rounded"
                                />
                                <span className="text-sm text-gray-700 font-medium">
                                    Remember me for 24 hours (keep me logged in)
                                </span>
                            </label>
                        </div>

                        {loginError && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                                {loginError}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loginLoading}
                            className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition disabled:opacity-50"
                        >
                            {loginLoading ? 'Logging in...' : 'Login'}
                        </button>
                    </form>

                    <div className="mt-6 text-center">
                        <a href="/" className="text-sm text-purple-600 hover:text-purple-700">
                            ← Back to Homepage
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600 text-lg">Loading CRM Dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Header */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">🤖 AI Business Manager</h1>
                            <p className="text-gray-600 mt-1">Real-time CRM & Marketing Automation Dashboard</p>
                        </div>
                        {/* Emergency Stop Button & Logout */}
                        <div className="flex items-center space-x-4">
                            {emergencyStop ? (
                                <button
                                    onClick={handleResume}
                                    className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition shadow-lg"
                                >
                                    ▶️ Resume Automation
                                </button>
                            ) : (
                                <button
                                    onClick={handleEmergencyStop}
                                    className="px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition shadow-lg animate-pulse"
                                >
                                    🛑 EMERGENCY STOP
                                </button>
                            )}
                            <button
                                onClick={handleLogout}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition"
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8 overflow-x-auto">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`${
                                activeTab === 'overview'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            📊 Overview
                        </button>
                        <button
                            onClick={() => setActiveTab('ai-content')}
                            className={`${
                                activeTab === 'ai-content'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            🤖 AI Content Generator
                        </button>
                        <button
                            onClick={() => setActiveTab('approval-queue')}
                            className={`${
                                activeTab === 'approval-queue'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            📋 Approval Queue
                        </button>
                        <button
                            onClick={() => setActiveTab('service-status')}
                            className={`${
                                activeTab === 'service-status'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            🎛️ Service Status
                        </button>
                        <button
                            onClick={() => setActiveTab('marketing')}
                            className={`${
                                activeTab === 'marketing'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            📧 Marketing
                        </button>
                        <button
                            onClick={() => setActiveTab('ai-decisions')}
                            className={`${
                                activeTab === 'ai-decisions'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            🧠 AI Decisions
                        </button>
                        <button
                            onClick={() => setActiveTab('system-status')}
                            className={`${
                                activeTab === 'system-status'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            ⚙️ System Status
                        </button>
                    </nav>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {activeTab === 'overview' && (
                    <OverviewTab metrics={metrics} />
                )}
                {activeTab === 'ai-content' && (
                    <AIContentGenerator onContentGenerated={handleContentGenerated} />
                )}
                {activeTab === 'approval-queue' && (
                    <ContentApprovalQueue />
                )}
                {activeTab === 'service-status' && (
                    <AIServiceStatus />
                )}
                {activeTab === 'marketing' && (
                    <MarketingDashboard />
                )}
                {activeTab === 'ai-decisions' && (
                    <AIDecisionsTab decisions={aiDecisions} />
                )}
                {activeTab === 'system-status' && (
                    <SystemStatusTab status={systemStatus} />
                )}
            </div>
        </div>
    );
};

// Overview Tab Component
const OverviewTab = ({ metrics }) => {
    if (!metrics) return <div>Loading metrics...</div>;

    return (
        <div className="space-y-6">
            {/* Metric Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard
                    title="Email Campaigns"
                    value={metrics.email_campaigns_sent || 0}
                    change="+12%"
                    icon="📧"
                    color="blue"
                />
                <MetricCard
                    title="Social Posts"
                    value={metrics.social_posts_published || 0}
                    change="+8%"
                    icon="📱"
                    color="green"
                />
                <MetricCard
                    title="Blog Posts"
                    value={metrics.blog_posts_generated || 0}
                    change="+5%"
                    icon="📝"
                    color="purple"
                />
                <MetricCard
                    title="Engagement Rate"
                    value={`${metrics.engagement_rate || 0}%`}
                    change="+3.2%"
                    icon="💹"
                    color="indigo"
                />
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Campaign Performance */}
                <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-xl font-semibold mb-4">Campaign Performance (7 Days)</h2>
                    <div className="space-y-2">
                        {metrics.campaign_performance && metrics.campaign_performance.map((day, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">{day.date}</span>
                                <div className="flex items-center space-x-4">
                                    <span className="text-sm text-blue-600">📧 {day.email_opens}</span>
                                    <span className="text-sm text-green-600">📱 {day.social_engagement}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* User Segments */}
                <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-xl font-semibold mb-4">Customer Segments</h2>
                    <div className="space-y-3">
                        {metrics.user_segments && metrics.user_segments.map((segment, idx) => (
                            <div key={idx}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium text-gray-700">{segment.segment}</span>
                                    <span className="text-sm text-gray-600">{segment.count} users</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className="bg-indigo-600 h-2 rounded-full"
                                        style={{ width: `${(segment.count / metrics.total_users) * 100}%` }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

// AI Decisions Tab Component
const AIDecisionsTab = ({ decisions }) => {
    return (
        <div className="bg-white rounded-lg shadow">
            <div className="p-6">
                <h2 className="text-xl font-semibold mb-4">Recent AI Decisions & Automated Actions</h2>
                <div className="space-y-4">
                    {decisions && decisions.length > 0 ? (
                        decisions.map((decision, index) => (
                            <div
                                key={index}
                                className={`border-l-4 pl-4 py-3 ${
                                    decision.status === 'success'
                                        ? 'border-green-500 bg-green-50'
                                        : 'border-red-500 bg-red-50'
                                }`}
                            >
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <p className="font-medium text-gray-900">{decision.action}</p>
                                        <p className="text-sm text-gray-600 mt-1">{decision.reason}</p>
                                    </div>
                                    <div className="text-right ml-4">
                                        <span className="text-xs text-gray-500">
                                            {new Date(decision.timestamp).toLocaleString()}
                                        </span>
                                        <div className="mt-1">
                                            {decision.status === 'success' ? (
                                                <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                                                    ✓ Success
                                                </span>
                                            ) : (
                                                <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded-full">
                                                    ✗ Failed
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            <p className="text-lg">No AI decisions logged yet</p>
                            <p className="text-sm mt-2">Automated actions will appear here</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// System Status Tab Component
const SystemStatusTab = ({ status }) => {
    if (!status) return <div>Loading system status...</div>;

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatusCard
                    title="Content Engine"
                    status={status.content_engine?.status}
                    lastRun={status.content_engine?.last_run}
                    mode={status.content_engine?.mode}
                />
                <StatusCard
                    title="Campaign Manager"
                    status={status.campaign_manager?.status}
                    lastRun={status.campaign_manager?.last_run}
                    mode={status.campaign_manager?.mode}
                />
                <StatusCard
                    title="Customer Intelligence"
                    status={status.customer_intelligence?.status}
                    lastRun={status.customer_intelligence?.last_run}
                    mode={status.customer_intelligence?.mode}
                />
            </div>

            {/* Automation Status */}
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">Automation Workflows</h2>
                <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                            <p className="font-medium">Morning Routine</p>
                            <p className="text-sm text-gray-600">Daily at 6:00 AM</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs ${
                            status.automation?.morning_routine_enabled
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                        }`}>
                            {status.automation?.morning_routine_enabled ? '✓ Enabled' : '⏸ Disabled'}
                        </span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                            <p className="font-medium">Evening Routine</p>
                            <p className="text-sm text-gray-600">Daily at 10:00 PM</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs ${
                            status.automation?.evening_routine_enabled
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                        }`}>
                            {status.automation?.evening_routine_enabled ? '✓ Enabled' : '⏸ Disabled'}
                        </span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                            <p className="font-medium">Real-time Monitoring</p>
                            <p className="text-sm text-gray-600">Every 5 minutes</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs ${
                            status.automation?.realtime_monitoring_enabled
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                        }`}>
                            {status.automation?.realtime_monitoring_enabled ? '✓ Enabled' : '⏸ Disabled'}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Reusable Metric Card Component
const MetricCard = ({ title, value, change, icon, color }) => {
    const colorClasses = {
        blue: 'border-blue-500',
        green: 'border-green-500',
        purple: 'border-purple-500',
        indigo: 'border-indigo-500'
    };

    return (
        <div className={`bg-white rounded-lg shadow p-6 border-l-4 ${colorClasses[color]}`}>
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm text-gray-600">{title}</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
                    <p className="text-sm text-green-600 mt-2">{change}</p>
                </div>
                <span className="text-5xl">{icon}</span>
            </div>
        </div>
    );
};

// Reusable Status Card Component
const StatusCard = ({ title, status, lastRun, mode }) => {
    return (
        <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">{title}</h3>
                <span className={`px-3 py-1 rounded-full text-xs ${
                    status === 'operational'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                }`}>
                    {status === 'operational' ? '✓ Operational' : '✗ Error'}
                </span>
            </div>
            <div className="space-y-2">
                <p className="text-sm text-gray-600">
                    Last run: {lastRun ? new Date(lastRun).toLocaleString() : 'Never'}
                </p>
                {mode && (
                    <p className="text-xs text-gray-500">
                        Mode: <span className={mode === 'live' ? 'text-green-600' : 'text-yellow-600'}>
                            {mode === 'live' ? '🟢 Live' : '🟡 Placeholder'}
                        </span>
                    </p>
                )}
            </div>
        </div>
    );
};

export default CEODashboard;
